import { useMemo } from 'react';

import { STATIC_STUDIO_CARDS } from 'constants/aiStudio';
import { getColumnKeys, NavScreenGroup, useAccessibleNavScreens } from 'hooks/nav/useAccessibleNavScreens';
import { StudioGroup } from 'types/landing';

/**
 * A group needs a key that is stable across renders and unique in the grid. `id` is the config's
 * own handle; the positional path prefix and then the label are fallbacks for tenants that have
 * not added `id` yet.
 */
const resolveKey = (group: NavScreenGroup, index: number): string =>
    group?.id || group?.path?.[0] || group?.label || `group_${index}`;

const toStudioGroup = (group: NavScreenGroup, index: number): StudioGroup => ({
    key: resolveKey(group, index),
    label: group.label,
    description: group.description,
    icon: group.icon,
    // The column label always renders here, unlike NavPanelGroup which gates it on
    // `showSubHeader`: the detail panel has the width for "Diagnostics" / "Predictive" headers
    // and the 220px nav rail does not.
    columns: getColumnKeys(group).map((key, columnIndex) => ({
        label: key,
        items: group.options[key].map((item) => ({
            ...item,
            // Identical to the expression NavPanelGroup.tsx uses: `item.url` is the route
            // `screenDetailsWithRoutes` registers, and the positional `group.path` prefix only
            // covers options that register no route of their own. Resolved here so the panel has
            // one source of truth and the two surfaces cannot drift apart.
            url: item.url ?? `/${group.path?.[columnIndex]}/${item.path}`,
        })),
    })),
});

/**
 * Cards for the AI Intelligent Studio, in tenant config order.
 *
 * Everything hard is already done by `useAccessibleNavScreens`: it reads `screens` off the
 * config, filters by `userAccessScope` then by `commonConfig.accessibleScreens` (dropping groups
 * with no surviving children), and resolves each option's absolute route from
 * `screenDetailsWithRoutes` onto `entry.url`. Reusing it is what keeps the Studio and the Navbar
 * V2 panel from ever disagreeing about where a screen lives.
 */
export const useStudioGroups = (): StudioGroup[] => {
    const groups = useAccessibleNavScreens();

    return useMemo(() => [...groups.map(toStudioGroup), ...STATIC_STUDIO_CARDS], [groups]);
};

export default useStudioGroups;
